import type { OperationModel, OperationResponse } from './OperationModel';
import type { PaymentModel, PaymentModelFR } from './PaymentModel';
import type { FiscalOptionEnumFR } from './enums/FiscalOptionEnum';
import type { OperationLegalStatusEnumFR } from './enums/OperationLegalStatusEnum';
import {
	PEAAccountTypeEnum,
	PEAAccountTypeEnumFR,
	translatePEAAccountType
} from './enums/PEAAccountTypeEnum';
import type { FileModel, FileModelFR } from './FileModel';
import {
	SubscriptionStatusEnum,
	SubscriptionStatusEnumFR,
	translateSubscriptionStatus
} from './enums/SubscriptionStatusEnum';
import { FiscalOptionEnum, translateFiscalOption } from './enums/FiscalOptionEnum';
import {
	OperationLegalStatusEnum,
	translateOperationLegalStatus
} from './enums/OperationLegalStatusEnum';
import { translateFileModel, translateFileModelArray } from './FileModel';
import { translatePaymentModel } from './PaymentModel';
import { translateOperationResponse } from './OperationModel';
import {
	translateSubscriptionLinksResponse,
	type SubscriptionLinksModel,
	type SubscriptionLinksResponse
} from './LinksModel';
import type { StandalonePaymentModel } from './StandalonePaymentModel';
import type { SignatureModel, SignatureAPIModel } from './SignatureModel';
import { translateSignatureResponse } from './SignatureModel';
import type { DetailedSubscriptionStatus } from '@app/models/api/enums/DetailedSubscriptionStatusEnum';
import { OperationFinancialInstrumentEnum } from '@app/models/api/enums/OperationFinancialInstrumentEnum';
import type { TargetTypeEnum } from './enums/TargetTypeEnum';
import type { OperationsTypeInteretsEnum } from './enums/ObligationsTypeInteretsEnum';

export type SubscriptionResponse = {
	id: number;
	operationTrancheId: number;
	dateCreation: string;
	statut: SubscriptionStatusEnumFR;
	detailedStatut: DetailedSubscriptionStatus;
	montant: number;
	devise: string;
	montantFraisTTC: number;
	operation: OperationResponse;
	paiement: PaymentModelFR;
	optionFiscale: FiscalOptionEnumFR;
	mandatRecu: boolean;
	pouvoirRecu: boolean;
	situationJuridique: OperationLegalStatusEnumFR;
	isOPC: boolean;
	informationsRemplies: boolean;
	typeComptePEA: PEAAccountTypeEnumFR;
	numeroComptePEA: string;
	etablissementBancaireComptePEA: string;
	ordreDesCheques: string;
	referenceVirement: string;
	codePromo: string;
	standalonePayments: StandalonePaymentModel[];
	bsSigne: FileModelFR;
	signatureBs: SignatureAPIModel;
	signatureProcurationOCA: SignatureAPIModel;
	documents: FileModelFR[];
	_links: SubscriptionLinksResponse;
	nbParts: number;
	prixPart?: number;
	dateSortie: Date;
	gains: number;
	coefficientMultiplicateur: number;
	meteo: number;
	commentaireMeteo: string;
	dateMajMeteo: Date;
	plusValueLatente: number;
	valeurEstimeeADate: number;
	pourcentageDetenu: number;
	commentaireTRI: string;
	dateDebutInterets: Date;
	pourcentageRendementAnnuelMin: number;
	pourcentageRendementAnnuelMax: number;
	dateFinInterets: Date;
	valeurEstimeeAEcheance: number;
	typeCible: TargetTypeEnum;
	secteur: string;
	dateInvestissement: Date;
	interetsAPercevoir: number;
	interetsBruts: number;
	interetsNets: number;
	dateEcheance?: Date;
	capitalRestantDu?: number;
	typeInterets?: OperationsTypeInteretsEnum;
	taux?: number;
	dureeEnMois?: string;
	echeanceDate: Date;
};

export type SubscriptionModel = {
	id: number;
	operationTrancheId: number;
	creationDate: string | Date;
	status: SubscriptionStatusEnum;
	amount: number;
	currency: string;
	amountCostsVATIncluded: number;
	operation: OperationModel;
	payment: PaymentModel;
	fiscalOption: FiscalOptionEnum;
	isWarrantReceived: boolean;
	isPowerReceived: boolean;
	legalStatus: OperationLegalStatusEnum;
	isOPC: boolean;
	isInfosFilled: boolean;
	PEAAccountType: PEAAccountTypeEnum;
	PEAAccountNumber: string;
	bankPEAAccount: string;
	chequesOrder: string;
	bankTransferReference: string;
	codePromo: string;
	standalonePayments: StandalonePaymentModel[];
	subscriptionForm: FileModel /* Le lien dans ce model dans url correspond au links / équivalent à BS Signé uniquement */;
	subscriptionSignature: SignatureModel;
	proxySignatureOCA: SignatureModel;
	documents: FileModel[];
	links?: SubscriptionLinksModel;
	numParts: number;
	oneShareAmount?: number;
	financialInstrument?: OperationFinancialInstrumentEnum;
	outgoing?: boolean;
	action?: string;
	amountInvested?: number;
	multiplierCoefficient: number;
	weather: number;
	latentCapitalGains: number;
	estimatedValueToDate: number;
	weatherUpdateDate: Date;
	weatherComment: string;
	percentageOwned: number;
	commentTRI: string;
	startInterestsDate: Date;
	percentageYieldTPMin: number;
	percentageYieldTPMax: number;
	endInterestsDate: Date;
	estimatedValueAtMaturity: number;
	targetType: TargetTypeEnum;
	sectorType: string;
	investmentDate: Date;
	remainingCapital?: number;
	interestType?: OperationsTypeInteretsEnum;
	rate?: number;
	durationInMonths?: string;
	echeanceDate?: Date;
	releaseDate: Date;
	gains: number;
	interestToBeReceived: number;
	grossInterest: number;
	netInterest: number;
};

export const translateSubscriptionResponse = (sr: SubscriptionResponse): SubscriptionModel => {
	return {
		id: sr.id,
		operationTrancheId: sr.operationTrancheId,
		creationDate: sr.dateCreation,
		status: translateSubscriptionStatus(sr.statut),
		amount: sr.montant,
		currency: sr.devise,
		amountCostsVATIncluded: sr.montantFraisTTC,
		operation: translateOperationResponse(sr.operation),
		payment: translatePaymentModel(sr.paiement),
		fiscalOption: translateFiscalOption(sr.optionFiscale),
		isWarrantReceived: sr.mandatRecu,
		isPowerReceived: sr.pouvoirRecu,
		legalStatus: translateOperationLegalStatus(sr.situationJuridique),
		isOPC: sr.isOPC,
		isInfosFilled: sr.informationsRemplies,
		PEAAccountType: translatePEAAccountType(sr.typeComptePEA),
		PEAAccountNumber: sr.numeroComptePEA,
		bankPEAAccount: sr.etablissementBancaireComptePEA,
		chequesOrder: sr.ordreDesCheques,
		bankTransferReference: sr.referenceVirement,
		codePromo: sr.codePromo,
		standalonePayments: sr.standalonePayments,
		subscriptionForm: translateFileModel(sr.bsSigne),
		subscriptionSignature: translateSignatureResponse(sr.signatureBs),
		proxySignatureOCA: translateSignatureResponse(sr.signatureProcurationOCA),
		documents: translateFileModelArray(sr.documents),
		numParts: sr.nbParts,
		oneShareAmount: sr.prixPart,
		grossInterest: sr.interetsBruts,
		netInterest: sr.interetsNets,
		interestType: sr.typeInterets,
		rate: sr.taux,
		durationInMonths: sr.dureeEnMois,
		releaseDate: sr.dateSortie,
		gains: sr.gains,
		targetType: sr.typeCible,
		investmentDate: sr.dateInvestissement,
		sectorType: sr.secteur,
		startInterestsDate: sr.dateDebutInterets,
		percentageYieldTPMin: sr.pourcentageRendementAnnuelMin,
		percentageYieldTPMax: sr.pourcentageRendementAnnuelMax,
		endInterestsDate: sr.dateFinInterets,
		estimatedValueAtMaturity: sr.valeurEstimeeAEcheance,
		multiplierCoefficient: sr.coefficientMultiplicateur,
		interestToBeReceived: sr.interetsAPercevoir,
		// Default Weather score
		remainingCapital: sr.capitalRestantDu,
		weather: sr.meteo ?? 4,
		weatherComment: sr.commentaireMeteo,
		weatherUpdateDate: sr.dateMajMeteo,
		latentCapitalGains: sr.plusValueLatente,
		estimatedValueToDate: sr.valeurEstimeeADate,
		percentageOwned: sr.pourcentageDetenu,
		commentTRI: sr.commentaireTRI,
		echeanceDate: sr.dateEcheance,
		...(sr._links && { links: translateSubscriptionLinksResponse(sr._links) })
	};
};

export const translateSubscriptionResponseArray = (
	sr: SubscriptionResponse[]
): SubscriptionModel[] => {
	const subscriptions: SubscriptionModel[] = [];
	sr && sr.forEach((sr) => subscriptions.push(translateSubscriptionResponse(sr)));
	return subscriptions;
};
